import { FileLibraryGroup } from 'components/asset/asset_file_library/types';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { NoValue } from 'components/no_value/NoValue';
import {
    DirectoryEntry,
    DirectoryPagedDataSource,
    isFileInfoResponse,
    useDirectoryPrefetch,
} from 'custom_hooks/data_hooks/data_source/directoryDataSource';
import { FileLibraryActionsCell } from 'pages/Settings/FileLibrary/FileLibraryActionsCell';
import { FolderNameCell } from 'pages/Settings/FileLibrary/table/FolderNameCell';
import { ImageFileNameCell } from 'pages/Settings/FileLibrary/table/ImageFileNameCell';
import { useMemo } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { getId } from 'utils/data';

import { setFileFromLibraryQueryData } from '../hooks/useFileFromLibrary';
import { PathToIdMapping, usePrefetchFolderIdsInPath } from '../hooks/useFolderIdsInPath';
import { toDirentIdentityInfo } from '../path';

export type FileLibraryTableProps = Readonly<{
    enableModal?: boolean;
    dataSource: DirectoryPagedDataSource;
    group?: FileLibraryGroup;
    locationId?: string;
    corporationId?: string;
    foldersHierarchy: PathToIdMapping[];
}>;

export function FileLibraryTable({
    group,
    enableModal,
    dataSource,
    locationId,
    corporationId,
    foldersHierarchy,
}: FileLibraryTableProps) {
    const { reloadPage } = dataSource;

    const prefetchFolderIdsInPath = usePrefetchFolderIdsInPath();
    const { prefetch: prefetchDir } = useDirectoryPrefetch(dataSource);

    const columns = useMemo<TableColumn<DirectoryEntry>[]>(
        () => [
            {
                header: () => 'File Name',
                id: 'Name',
                value: ({ rowData }) => {
                    const identity = toDirentIdentityInfo(rowData);
                    return (
                        <>
                            {isFileInfoResponse(rowData) ? (
                                <ImageFileNameCell
                                    fileData={{
                                        ...identity,
                                        name: rowData.Name,
                                        thumbnailStorageKey:
                                            rowData.ThumbnailStorageFileName,
                                        mediaType: rowData.MediaType,
                                    }}
                                    enableModal={enableModal ?? false}
                                    prefetch={() => {
                                        setFileFromLibraryQueryData(rowData.Id, {
                                            ...rowData,
                                            GroupIds: rowData.Groups.map(
                                                (g) => g.GroupId,
                                            ),
                                        });
                                    }}
                                />
                            ) : (
                                <FolderNameCell
                                    folderData={{
                                        ...identity,
                                        name: rowData.Name,
                                    }}
                                    prefetch={() => {
                                        prefetchFolderIdsInPath({
                                            path: rowData.Path,
                                            id: rowData.Id,
                                            corporationId,
                                            locationId,
                                        });
                                        prefetchDir({ folderId: rowData.Id });
                                    }}
                                />
                            )}
                            <DebugValue value={rowData} description="file info" />
                        </>
                    );
                },

                sortOn: 'Name',
                primary: true,
                locked: true,
                valueClassName: 'flex align-center',
            },
            {
                header: () => 'Description',
                id: 'Description',
                value: ({ rowData }) => (
                    <div className="ellipsis-1-line" title={rowData.Description ?? ''}>
                        {rowData.Description ?? <NoValue />}
                    </div>
                ),
                primary: false,
                valueClassName: `${
                    enableModal ? 'max-cell-width-modal' : 'max-cell-width'
                }`,
                locked: true,
            },
            {
                header: () => 'Uploaded',
                id: 'Uploaded',
                value: ({ rowData }) => <DateRelativeToNow date={rowData.CreationDate} />,
                sortOn: 'CreationDate',
                primary: false,
                locked: true,
            },
            {
                header: () => 'Actions',
                id: 'Actions',
                locked: true,
                value: ({ rowData }) => (
                    <FileLibraryActionsCell
                        group={group}
                        enableModal={enableModal ?? false}
                        rowData={rowData}
                        onChange={reloadPage}
                        locationId={locationId}
                        corporationId={corporationId}
                        foldersHierarchy={foldersHierarchy}
                    />
                ),
            },
        ],
        [
            enableModal,
            prefetchFolderIdsInPath,
            prefetchDir,
            group,
            reloadPage,
            locationId,
            corporationId,
            foldersHierarchy,
        ],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No files found."
        />
    );
}
